import React, { useState, useEffect } from "react";
import Image from "next/image";
import { CartDisplay } from "../cart/cartDisplay.comp";
import Link from "next/link";
import { useTranslation } from "../../core/translation.core";

interface HeaderProps {
  SearchBar: React.ReactNode;
  onMobileMenuToggle?: (isOpen: boolean) => void;
}

export default function NavHeader({ SearchBar, onMobileMenuToggle }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, msg } = useTranslation();

  const toggleMenu = () => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);
    onMobileMenuToggle?.(newState);
  };

  const Logo = () => (
    <Link href="/" className="block">
      <div className="relative h-12 w-12">
        <Image
          src="/images/logo-square.webp"
          alt="Gubu Guru Logo"
          fill
          sizes="48px"
          className="rounded-full shadow-sm hover:shadow-md transition-shadow duration-200 object-cover cursor-pointer"
          priority
        />
      </div>
    </Link>
  );

  return (
    <header className="w-full bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          {/* Left section */}
          <div className="w-full flex flex-col md:items-start md:w-auto md:flex-1 md:flex-row md:gap-4">
            {/* Mobile header */}
            <div className="w-full grid grid-cols-3 items-center md:hidden py-2">
              <div className="flex justify-start">
                <button
                  onClick={toggleMenu}
                  className="p-2 text-gray-600 hover:text-gray-900"
                  aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                    />
                  </svg>
                </button>
              </div>
              <div className="flex justify-center">
                <Logo />
              </div>
              <div className="flex justify-end">
                <CartDisplay underlineOffset={-1} iconSize={6} textSize="base" />
              </div>
            </div>

            {/* Desktop header */}
            <div className="hidden md:flex items-center md:flex-shrink-0">
              <Logo />
            </div>
            <div className="hidden md:block md:flex-1 max-w-md">{SearchBar}</div>
          </div>

          {/* Right section - Desktop navigation */}
          <div className="hidden md:flex items-center gap-6">
            <nav className="flex items-center gap-6">
              <NavigationLinksComp />
            </nav>
            <div className="flex-shrink-0 w-[100px]">
              <CartDisplay iconSize={5} textSize="base" underlineOffset={-8} className="justify-end" />
            </div>
          </div>
        </div>

        {/* Mobile menu and search - Only render when menu is open */}
        <div className={`md:hidden transition-all duration-200 ${isMenuOpen ? "max-h-screen" : "max-h-0 overflow-hidden"}`}>
          <div className="py-3 space-y-3 border-t border-gray-100">
            <NavigationLinksComp isMobile />
          </div>
          {/* Only show SearchBar in mobile menu when menu is open */}
          {isMenuOpen && <div className="w-full py-3">{SearchBar}</div>}
        </div>
      </div>
    </header>
  );
}

function NavigationLinksComp({ isMobile = false }: { isMobile?: boolean }) {
  const { t, msg } = useTranslation();

  const baseStyles = "text-gray-700 hover:text-gray-900 text-sm font-medium tracking-wide whitespace-nowrap";
  const desktopStyles = "hover:scale-105 transition-transform duration-200";
  const mobileStyles = "block py-2";

  // Define navigation links with their URLs using translations
  const navigationLinks = [
    { text: t(msg.menu.noWaste), url: "/article/no-waste" },
    { text: t(msg.menu.charity), url: "/article/charity" },
    { text: t(msg.menu.guruFun), url: "/guru-fun" },
  ];

  return (
    <>
      {navigationLinks.map((link) => (
        <Link key={link.text} href={link.url} className={`${baseStyles} ${isMobile ? mobileStyles : desktopStyles}`}>
          {link.text}
        </Link>
      ))}
    </>
  );
}
